// src/pages/AcceptTenantInvitation.jsx
import {useState, useEffect} from 'react';
import {useSearchParams, useNavigate} from 'react-router-dom';
// import {tenantServiceSendInviteCallback} from 'src/apis';
import {tenantServiceSendInviteCallback} from 'src/apis/autoGenerate/services';
import {Paper, Box, Grid, Typography, CssBaseline, Button, CircularProgress} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import DownloadIcon from '@mui/icons-material/Download';
import {ReactComponent as Logo} from 'src/assets/logo.svg';
import loginBackground from 'src/assets/loginBackground.png';
import AppleIcon from '@mui/icons-material/Apple';
import AndroidIcon from '@mui/icons-material/Android';

function AcceptTenantInvitation() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');

  useEffect(() => {
    const id = searchParams.get('id');
    const token = searchParams.get('token');
    const email = searchParams.get('email');

    if (!id || !token) {
      setStatus('error');
      setError('Invalid invitation link');
      return;
    }

    // Decode the email if it exists
    const decodedEmail = email ? decodeURIComponent(email) : '';

    if (!decodedEmail) {
      setStatus('error');
      setError('Email is required to accept this invitation');
      return;
    }

    const acceptInvitation = async () => {
      try {
        // Use verifyCode instead of token parameter name
        await tenantServiceSendInviteCallback(id, {
          verifyCode: token,
          email: decodedEmail,
          id: id,
        });

        setStatus('success');
      } catch (err) {
        setStatus('error');
        setError(err.response?.data?.error || 'An unexpected error occurred');
      }
    };

    acceptInvitation();
  }, [searchParams]);

  // Content based on status
  const renderContent = () => {
    switch (status) {
      case 'loading':
        return (
          <Box display="flex" flexDirection="column" alignItems="center" p={3}>
            <CircularProgress size={60} sx={{mb: 3}} />
            <Typography variant="h5" gutterBottom>
              Processing your invitation...
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Please wait while we confirm your details.
            </Typography>
          </Box>
        );
      case 'success':
        return (
          <Box display="flex" flexDirection="column" alignItems="center" p={3}>
            <CheckCircleOutlineIcon sx={{fontSize: 60, color: 'success.main', mb: 3}} />
            <Typography variant="h5" gutterBottom>
              Invitation Accepted!
            </Typography>
            <Typography variant="body1" color="text.secondary" align="center" mb={4}>
              You have successfully joined the organization.
            </Typography>

            <Box sx={{width: '100%', backgroundColor: '#f5f7fa', p: 3, borderRadius: 2, mb: 3}}>
              <Typography variant="body1" fontWeight="medium" mb={2}>
                <DownloadIcon sx={{verticalAlign: 'middle', mr: 1}} />
                Download our app to access your account:
              </Typography>

              <Grid container spacing={2} justifyContent="center">
                <Grid item>
                  <Button
                    variant="contained"
                    startIcon={<AppleIcon />}
                    href="[APP_STORE_LINK]"
                    sx={{
                      backgroundColor: '#000',
                      '&:hover': {backgroundColor: '#333'},
                      borderRadius: 2,
                    }}
                  >
                    App Store
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    variant="contained"
                    startIcon={<AndroidIcon />}
                    href="[PLAY_STORE_LINK]"
                    sx={{
                      backgroundColor: '#3ddc84',
                      color: '#000',
                      '&:hover': {backgroundColor: '#32b66c'},
                      borderRadius: 2,
                    }}
                  >
                    Google Play
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Box>
        );
      case 'error':
      default:
        return (
          <Box display="flex" flexDirection="column" alignItems="center" p={3}>
            <ErrorOutlineIcon sx={{fontSize: 60, color: 'error.main', mb: 3}} />
            <Typography variant="h5" gutterBottom>
              Something went wrong
            </Typography>
            <Typography variant="body1" color="text.secondary" align="center" mb={3}>
              {error ||
                "We couldn't process your invitation. It may have expired or already been used."}
            </Typography>
            <Button variant="contained" onClick={() => navigate('/login')} sx={{mt: 2}}>
              Return to Login
            </Button>
          </Box>
        );
    }
  };

  return (
    <Grid container component="main" sx={{height: '100vh'}}>
      <CssBaseline />
      <Grid
        item
        container
        xs={12}
        sm={8}
        md={7}
        component={Paper}
        justifyContent="center"
        alignItems="center"
        sx={{backgroundColor: '#fafafa'}}
      >
        <Paper
          elevation={3}
          sx={{
            width: {xs: '90%', sm: 500},
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              p: 3,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              borderBottom: '1px solid #eee',
            }}
          >
            <Logo style={{height: 50, marginBottom: 16}} />
          </Box>
          {renderContent()}
        </Paper>
      </Grid>
      <Grid
        item
        xs={false}
        sm={4}
        md={5}
        sx={{
          backgroundImage: `url(${loginBackground})`,
          backgroundRepeat: 'no-repeat',
          backgroundColor: t =>
            t.palette.mode === 'light' ? t.palette.grey[50] : t.palette.grey[900],
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
    </Grid>
  );
}

export default AcceptTenantInvitation;
